// Line-decoration extension that lights up the source range(s) of code
// currently producing audio. Generic — the extension knows nothing about
// Strudel or haps; callers dispatch `setPlayingRanges` with `{from, to}`
// pairs and the matching lines get a `cm-playing-line` class.
//
// Two surfaces consume this today:
//   - SourcePane: ranges derived from currently-firing haps mapped back
//     to the statement that emits each voice.
//   - PlayableSnippet (Learn): the snippet's whole doc range while its
//     preview is active. Snippets are tiny (1–3 lines), so "the snippet
//     is live" doubles as "this is the playing line".
//
// Reactive bridge mirrors playableSoloGutter: a StateField holds the
// active ranges; React dispatches `setPlayingRanges` to swap them. The
// decoration set is rebuilt only when ranges change or the doc length
// shifts under us, so steady-state playback at 60fps is cheap.

import { StateEffect, StateField, RangeSetBuilder } from '@codemirror/state';
import { Decoration, EditorView, type DecorationSet } from '@codemirror/view';
import type { Extension } from '@codemirror/state';

export type PlayingRange = { from: number; to: number };

export const setPlayingRanges = StateEffect.define<PlayingRange[]>();

const lineDeco = Decoration.line({
  attributes: { class: 'cm-playing-line' },
});

const rangesField = StateField.define<PlayingRange[]>({
  create: () => [],
  update(value, tr) {
    let next = value;
    for (const effect of tr.effects) {
      if (effect.is(setPlayingRanges)) next = effect.value;
    }
    // We deliberately skip mapping ranges through doc changes: the
    // ticker dispatches a fresh set every ~16ms, so a transient
    // mismatch after a keystroke self-corrects on the next frame.
    // The buildSet step clamps to docLen, so we can't paint past EOF.
    return next;
  },
});

function buildSet(
  ranges: PlayingRange[],
  doc: { length: number; lineAt: (pos: number) => { number: number; to: number }; line: (n: number) => { from: number } },
): DecorationSet {
  if (ranges.length === 0) return Decoration.none;
  const docLen = doc.length;
  // Collect the unique line numbers covered by any active range.
  const lines = new Set<number>();
  for (const r of ranges) {
    const from = Math.max(0, Math.min(r.from, docLen));
    const to = Math.max(from, Math.min(r.to, docLen));
    if (to <= from && from < docLen) {
      lines.add(doc.lineAt(from).number);
      continue;
    }
    let pos = from;
    while (pos <= to) {
      const line = doc.lineAt(pos);
      lines.add(line.number);
      if (line.to >= to) break;
      pos = line.to + 1;
    }
  }
  const sorted = Array.from(lines).sort((a, b) => a - b);
  const builder = new RangeSetBuilder<Decoration>();
  for (const lineNo of sorted) {
    const line = doc.line(lineNo);
    builder.add(line.from, line.from, lineDeco);
  }
  return builder.finish();
}

const decorationsField = StateField.define<DecorationSet>({
  create: (state) =>
    buildSet(state.field(rangesField, false) ?? [], state.doc),
  update(value, tr) {
    let rangesChanged = false;
    for (const effect of tr.effects) {
      if (effect.is(setPlayingRanges)) {
        rangesChanged = true;
        break;
      }
    }
    if (!rangesChanged && !tr.docChanged) return value;
    return buildSet(tr.state.field(rangesField, false) ?? [], tr.state.doc);
  },
  provide: (f) => EditorView.decorations.from(f),
});

export const playingLinesExtension: Extension = [rangesField, decorationsField];

/** Convenience: dispatch a ranges update on a view. Safe no-op if the
 *  view is null/destroyed. */
export function dispatchPlayingRanges(
  view: EditorView | null,
  ranges: PlayingRange[],
): void {
  if (!view) return;
  try {
    view.dispatch({ effects: setPlayingRanges.of(ranges) });
  } catch {
    // View destroyed mid-dispatch — ignore.
  }
}

/** Stable equality check for two range arrays. Cheaper than rebuilding
 *  the decoration set when the ticker re-derives the same set frame-
 *  over-frame in steady-state playback. */
export function rangesEqual(a: PlayingRange[], b: PlayingRange[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i]!;
    const bi = b[i]!;
    if (ai.from !== bi.from || ai.to !== bi.to) return false;
  }
  return true;
}
